import { LapiError } from '@iwoca/lapi-client/lendingAPI';
import { isAfter, isBefore, startOfMonth, sub } from 'date-fns';
import { keysIn } from 'lodash';
import { FieldError, FieldErrors } from 'react-hook-form';
import { isValidJSON } from 'utils/common/common';
import {
  TAddressType,
  TClientDetailsType,
  TSignupFormData,
} from './SignupForm.types';

export const dayRegex = /\b(0?[1-9]|[1-2][0-9]|[3][0-1])\b/;
export const monthRegex = /^(?:0?[1-9]|1[012])$/;

export const isOver18 = (year: string, month: string, day: string) => {
  const date18YearsAgo = sub(new Date(), { years: 18 });

  return isBefore(
    new Date(parseInt(year), parseInt(month) - 1, parseInt(day)),
    date18YearsAgo,
  );
};

export const isTradingDateInFuture = (month: string, year: string) => {
  const tradingMonth = new Date(parseInt(year), parseInt(month) - 1, 1);

  return isAfter(tradingMonth, startOfMonth(new Date()));
};

export const parseBackendErrorMessage = (error: unknown) => {
  const errorMessage =
    error instanceof LapiError && isValidJSON(error.message)
      ? JSON.parse(error.message).errors?.[0]
      : undefined;

  // If backend response is a validation error, show the validation message, otherwise, show the generic backend error message
  if (
    errorMessage &&
    errorMessage?.detail &&
    errorMessage?.source?.pointer &&
    errorMessage?.code === 'ValidationError'
  ) {
    const pointer = errorMessage.source.pointer;
    let errorField;

    // Company
    if (pointer.includes('type')) {
      errorField = 'company type';
    }
    if (pointer.includes('trading_from_date')) {
      errorField = 'trading start date';
    }
    if (pointer.includes('last_12_months_turnover')) {
      errorField = 'last 12 months turnover';
    }
    if (pointer.includes('vat_status')) {
      errorField = 'VAT status';
    }
    if (pointer.includes('trading_name')) {
      errorField = 'trading name';
    }
    if (pointer.includes('website')) {
      errorField = 'website';
    }

    // Applicants
    if (pointer.includes('first_name')) {
      errorField = 'first name';
    }
    if (pointer.includes('last_name')) {
      errorField = 'last name';
    }
    if (pointer.includes('number')) {
      errorField = 'phone number';
    }
    if (pointer.includes('email')) {
      errorField = 'email';
    }
    if (pointer.includes('date_of_birth')) {
      errorField = 'date of birth';
    }
    if (pointer.includes('residential_addresses')) {
      errorField = 'residential address';
    }
    if (pointer.includes('privacy_policy')) {
      errorField = 'privacy policy';
    }

    if (errorField) {
      return `Some details don't look right. Please check the ${errorField}.`;
    }
    // Adding this default message in case the validation error pointer does not match any of the fields (though it shouldn't happen).
    return "Some details don't look right.";
  } else {
    return 'Something went wrong, please try again later, or get in touch.';
  }
};

// When the card is hidden (e.g. the amount dropped below the threshold or the
// purpose changed), send `refinancing: null` so the backend clears any debts
// previously saved — without this the user has no way to remove them once
// submitted. When the card is shown, the form values pass through untouched.
export const clearHiddenRefinancing = (
  values: TSignupFormData,
  showRefinancing: boolean,
): TSignupFormData => {
  if (showRefinancing) return values;
  return { ...values, refinancing: null };
};

export const getFrontendValidationErrors = (
  error: FieldErrors<TSignupFormData>,
): string[] => {
  const errorList: string[] = [];

  const handleField = (field?: FieldError | FieldErrors<any>) => {
    if (!field || typeof field !== 'object') return;
    if ('message' in field && field.message) {
      errorList.push(field.message as string);
    }
  };

  const getAddressErrors = (addresses?: FieldErrors<TAddressType>[]) => {
    addresses?.forEach?.((address) => {
      if (!address) return;
      const addressKeys = keysIn(address) as (keyof TAddressType)[];
      addressKeys.forEach((key) => {
        handleField(address[key]);
      });
    });
  };

  const getPersonErrors = (person?: FieldErrors<TClientDetailsType>) => {
    if (!person) return;

    const personKeys = keysIn(person) as (keyof TClientDetailsType)[];
    personKeys.forEach((personKey) => {
      if (personKey === 'residentialAddresses') {
        getAddressErrors(
          person.residentialAddresses as FieldErrors<TAddressType>[],
        );
      }
      handleField(person[personKey] as FieldError);
    });
  };

  const getRefinancingErrors = (
    refinancing?: FieldErrors<NonNullable<TSignupFormData['refinancing']>>,
  ) => {
    if (!refinancing) return;

    // Cross-field rules (e.g. total debt exceeds the requested amount) attach
    // their message to the refinancing object itself rather than a named field.
    handleField(refinancing as FieldError);

    const refinancingKeys = keysIn(refinancing) as (keyof NonNullable<
      TSignupFormData['refinancing']
    >)[];
    refinancingKeys.forEach((key) => {
      if (key === 'debts') {
        (refinancing.debts as FieldErrors[] | undefined)?.forEach?.((debt) => {
          if (!debt) return;
          keysIn(debt).forEach((debtKey) =>
            handleField((debt as FieldErrors)[debtKey]),
          );
        });
        return;
      }
      handleField(refinancing[key] as FieldError);
    });
  };

  const errorKeys = keysIn(error) as (keyof TSignupFormData)[];
  errorKeys.forEach((errorKey) => {
    if (errorKey === 'guarantors') {
      error.guarantors?.forEach?.((guarantor) =>
        getPersonErrors(guarantor as FieldErrors<TClientDetailsType>),
      );
      return;
    }

    if (errorKey === 'applicant') {
      getPersonErrors(error.applicant as FieldErrors<TClientDetailsType>);
      return;
    }

    if (errorKey === 'refinancing') {
      getRefinancingErrors(
        error.refinancing as FieldErrors<
          NonNullable<TSignupFormData['refinancing']>
        >,
      );
      return;
    }

    handleField(error[errorKey]);
  });

  return errorList;
};
